import React, { Component, PureComponent } from 'react';

// keyCode constants
const BACKSPACE = 8;
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
const DELETE = 46;
const NUMBER_REGEXP = new RegExp(/\d/g); //Regex to check only numbers
const STRING_REGEXP = new RegExp(/\D/g); //Regex to check only string

/**
 * Doesn't really check if it's a style Object
 * Basic implemenetation to check if it's not a string
 * of classNames and is an Object
 */
const isStyleObject = obj => typeof obj === 'object';

class SingleOtpInput extends PureComponent {
	// Focus on first render
	// Only when shouldAutoFocus is true
	componentDidMount() {
		const {
			input,
			props: { focus, shouldAutoFocus }
		} = this;
		if (input && focus && shouldAutoFocus) {
			input.focus();
		}
	}

	componentDidUpdate(prevProps) {
		const {
			input,
			props: { focus }
		} = this;
		/**
		 * Check if focusedInput changed
		 * Prevent calling function if input already in focus
		 */
		if (prevProps.focus !== focus && input && focus) {
			input.focus();
		}
	}

	getClasses = (...classes) => classes.filter(c => !isStyleObject(c) && c !== false).join(' ');

	render() {
		const {
			separator,
			isLastChild,
			inputStyle,
			focus,
			isDisabled,
			hasErrored,
			errorStyle,
			focusStyle,
			disabledStyle,
			isInputNum,
			value,

			...rest
		} = this.props;

		const numValueLimits = isInputNum ? { min: 0, max: 9 } : {};

		return (
			<div style={{ display: 'flex', alignItems: 'center', paddingRight: 4 }}>
				<input
					style={Object.assign(
						{
							maxWidth: '40px',
							width: "100%",
							textAlign: 'center',
							paddingLeft: 0,
							paddingRight: 0,
							borderRadius: 0
						},
						isStyleObject(inputStyle) && inputStyle,
						focus && isStyleObject(focusStyle) && focusStyle,
						isDisabled && isStyleObject(disabledStyle) && disabledStyle,
						hasErrored && isStyleObject(errorStyle) && errorStyle
					)}
					className={this.getClasses(
						inputStyle,
						focus && focusStyle,
						isDisabled && disabledStyle,
						hasErrored && errorStyle
					)}
					type={isInputNum ? 'number' : 'tel'}
					{...numValueLimits}
					maxLength="1"
					ref={input => {
						this.input = input;
					}}
					disabled={isDisabled}
					value={value ? value : ''}
					{...rest}
				/>
				{!isLastChild && separator}
			</div>
		);
	}
}

class OtpInput extends Component {
	static defaultProps = {
		numInputs: 4,
		onChange: () => {},
		isDisabled: false,
		shouldAutoFocus: false
	};

	state = {
		activeInput: 0,
		value: this.props.value,
		isOtpReceived: false
	};

	UNSAFE_componentWillReceiveProps(nextProps) {
		if (nextProps.value !== this.props.value) {
			this.setState({ value: nextProps.value }, () => {
				const { numInputs, isLoading } = this.props;
				const { activeInput } = this.state;
				if (nextProps.value.length === numInputs && activeInput === 0 && !isLoading) {
					const otp = [];
					const clipboard = nextProps.value;
					this.copyOtpValueToInputs(clipboard, numInputs, activeInput, otp);
				}
			});
		}
	}

	getOtpValue = () => (this.props.value ? this.props.value.split('  ') : []);

	// Helper to return OTP from input
	handleOtpChange = otp => {
		const { onChange, isInputNum } = this.props;
		const otpValue = otp.join('  ');
		onChange(isInputNum ? Number(otpValue) : otpValue);
	};

	// Focus on input by index
	focusInput = input => {
		const { numInputs } = this.props;
		const activeInput = Math.max(Math.min(numInputs - 1, input), 0);
		this.setState({ activeInput });
	};

	// Focus on next input
	focusNextInput = () => {
		const { activeInput } = this.state;
		this.focusInput(activeInput + 1);
	};

	// Focus on previous input
	focusPrevInput = () => {
		const { activeInput } = this.state;
		this.focusInput(activeInput - 1);
	};

	// Change OTP value at focused input
	changeCodeAtFocus = value => {
		const { activeInput } = this.state;
		const otp = this.getOtpValue();
		if (!value) {
			otp[activeInput] = null;
			this.handleOtpChange(otp);
		} else {
			otp[activeInput] = value;
			this.handleOtpChange(otp);
		}
	};

	copyOtpValueToInputs(clipboard, numInputs, activeInput, otp) {
		let pastedData = [];
		if (NUMBER_REGEXP.test(clipboard)) {
			pastedData = clipboard.split('');
			// Paste data from focused input onwards
			for (let pos = 0; pos < numInputs; ++pos) {
				if (pos >= activeInput && pastedData.length > 0) {
					otp[pos] = pastedData.shift();
					this.focusInput(pos); //shift the position of cursor to next field
				}
			}
		} else {
			this.props.handlePastedDataIsNotNumber();
		}
		this.handleOtpChange(otp);
	}

	// Handle pasted OTP
	handleOnPaste = e => {
		e.preventDefault();
		const { numInputs } = this.props;
		const { activeInput } = this.state;
		const otp = this.getOtpValue();

		// Get pastedData in an array of max size (num of inputs - current position)
		const clipboard = e.clipboardData
			.getData('text/plain')
			.slice(0, numInputs - activeInput)
			.replace(STRING_REGEXP, '');
		this.copyOtpValueToInputs(clipboard, numInputs, activeInput, otp);
	};

	handleOnChange = e => {
		const CHECK_NUMBER_REGEXP = new RegExp(/\d/g);
		if (!this.props.isInputNum && CHECK_NUMBER_REGEXP.test(e.target.value)) {
			this.changeCodeAtFocus(e.target.value);
			e.preventDefault();
			this.focusNextInput();
		}
	};

	handleBackSpaceOrDelete(e) {
		e.preventDefault();
		this.changeCodeAtFocus('');
		this.focusPrevInput();
	}

	// Handle cases of backspace, delete, left arrow, right arrow
	handleOnKeyDown = e => {
		const otp = this.getOtpValue();
		if (otp[this.state.activeInput]) {
			this.focusNextInput();
		}
		if (e.keyCode === BACKSPACE || e.key === 'Backspace') {
			this.handleBackSpaceOrDelete(e);
		} else if (e.keyCode === DELETE || e.key === 'Delete') {
			this.handleBackSpaceOrDelete(e);
		} else if (e.keyCode === LEFT_ARROW || e.key === 'ArrowLeft') {
			e.preventDefault();
			this.focusPrevInput();
		} else if (e.keyCode === RIGHT_ARROW || e.key === 'ArrowRight') {
			e.preventDefault();
			this.focusNextInput();
		}
	};

	checkLength = e => {
		if (e.target.value.length > 1) {
			e.preventDefault();
			this.focusNextInput();
		}
	};

	renderInputs = () => {
		const { activeInput } = this.state;
		const {
			numInputs,
			inputStyle,
			focusStyle,
			separator,
			isDisabled,
			disabledStyle,
			hasErrored,
			errorStyle,
			shouldAutoFocus,
			isInputNum
		} = this.props;
		const otp = this.getOtpValue();
		const inputs = [];

		for (let i = 0; i < numInputs; i++) {
			inputs.push(
				<SingleOtpInput
					key={i}
					focus={activeInput === i}
					value={otp && otp[i]}
					onChange={this.handleOnChange}
					onKeyDown={this.handleOnKeyDown}
					onInput={this.checkLength}
					onPaste={this.handleOnPaste}
					onFocus={() => {
						this.setState({ activeInput: i });
					}}
					onBlur={() => {
						this.setState({ activeInput: -1 });
					}}
					separator={separator}
					inputStyle={inputStyle}
					focusStyle={focusStyle}
					isLastChild={i === numInputs - 1}
					isDisabled={isDisabled}
					disabledStyle={disabledStyle}
					hasErrored={hasErrored}
					errorStyle={errorStyle}
					shouldAutoFocus={shouldAutoFocus}
					isInputNum={isInputNum}
				/>
			);
		}

		return inputs;
	};

	render() {
		const { containerStyle } = this.props;
		const propStyle = Object.assign(
			{
				display: 'flex',
				justifyContent: 'space-between'
			},
			isStyleObject(containerStyle) && containerStyle
		);
		return (
			<div style={propStyle} className={!isStyleObject(containerStyle) && containerStyle}>
				{this.renderInputs()}
			</div>
		);
	}
}

export { OtpInput };
